import { FC, useContext, useEffect, useRef, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import AppContext from "../../../AppContext";
import useMultiLanguage from "../../../hook/useMultiLanguage";
import TextValidator from "../../components/input/text-validator";
import { GroupButton } from "../../components/GroupButton";
import { hasAuthority } from "../../utils/functionUtils";
import { PERMISSION_ABILITY, PERMISSIONS } from "../../../constants";
import Autocomplete from "../../components/input/autocomplete";
import { LIST_TYPE_SCHEDULE } from "../constants/approvalConfigConsts";
import { heightSelectMutil } from "../../components/input/autocomplete/styleJS";
import TableCollapseCustom, { columnNamesCollapseType } from "../../components/table-collapse-custom/TableCollapseCustom";
import { SelectEmployeeByDepartment } from "../../components/dialogs";
import { STATUS_NV } from "../../profile/constants/dialogChildConsts";
import { RESPONSE_STATUS_CODE } from "../../constants/moduleConsts";
import { toast } from "react-toastify";
import { createDayTruc, editDayTruc, getDayTruc } from "../../timesheet/services/timesheetServices";
import { IEmployeeListScheduleSelected, IEmployeeScheduleConfig, IScheduleConfig } from "../models/categoryModels";

interface IProps {
    open: boolean;
    handleClose: () => void;
    handleCloseAndSearch: () => void;
    scheduleConfigInfo: any;
    isView: boolean;
}

const ScheduleConfigForm: FC<IProps> = (props) => {
    const {
        open,
        handleClose,
        handleCloseAndSearch,
        scheduleConfigInfo,
        isView
    } = props;

    const { lang } = useMultiLanguage();
    const { setPageLoading } = useContext(AppContext);

    const employeeListRef = useRef<IEmployeeListScheduleSelected[]>([]);

    const [isEdit, setIsEdit] = useState<boolean>(!isView);
    const [employeeChecked, setEmployeeChecked] = useState<any[]>([]);
    const [employeeList, setEmployeeList] = useState<IEmployeeListScheduleSelected[]>([]);
    const [shouldOpenSelectEmployeeDialog, setShouldOpenSelectEmployeeDialog] = useState<boolean>(false);

    employeeListRef.current = employeeList;

    useEffect(() => {
        scheduleConfigInfo?.id && getDetailSchedule(scheduleConfigInfo?.id);
    }, [scheduleConfigInfo]);

    const getDetailSchedule = async (id: string) => {
        try {
            setPageLoading(true);
            const { data } = await getDayTruc(id);
            if (data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
                const typeSelected = LIST_TYPE_SCHEDULE(lang).find(type => type.code === data.data.type)
                formik.setValues({ ...formik.values, ...data.data, typeSelected });
                setEmployeeList(groupEmployeesByPhongBan(data.data.employeeList || []));
            }
        } catch (error) {
            toast.error(lang("GENERAL.ERROR"));
        } finally {
            setPageLoading(false);
        }
    };

    const handleSubmit = async (values: any) => {
        try {
            const listEmployee = employeeListRef.current.flatMap((item: any) =>
                item.data.map((emp: any) => ({
                    employeeId: emp.employeeId,
                    employeeName: emp.employeeName,
                    phongBanId: emp.phongBanId,
                    phongBanText: emp.phongBanText,
                    maNhanVien: emp.maNhanVien,
                    chucVuId: emp.chucVuId,
                    chucVuText: emp.chucVuText,
                    viTriCongViecId: emp.viTriCongViecId,
                    viTriCongViecText: emp.viTriCongViecText,
                }))
            );
            const dataSend = {
                name: values.name,
                code: values.code,
                type: values.typeSelected.code,
                employeeList: listEmployee
            };
            setPageLoading(true);
            const { data } = scheduleConfigInfo?.id
                ? await editDayTruc(scheduleConfigInfo.id, dataSend)
                : await createDayTruc(dataSend);
            if (data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
                toast.success(
                    scheduleConfigInfo.id
                        ? lang("TOAST.EDIT.SUCCESS")
                        : lang("TOAST.CREATE.SUCCESS")
                );
                handleCloseAndSearch();
            } else {
                toast.warning(data?.message);
            }
        } catch (err) {
            toast.error(lang("GENERAL.ERROR"));
        } finally {
            setPageLoading(false)
        }
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(lang("VALIDATION.REQUIRE")).nullable(),
        code: Yup.string().required(lang("VALIDATION.REQUIRE")).nullable(),
        typeSelected: Yup.object().required(lang("VALIDATION.REQUIRE")).nullable(),
    });

    const formik = useFormik<IScheduleConfig>({
        initialValues: { name: '', code: '', typeSelected: null, type: null, },
        validationSchema,
        onSubmit: handleSubmit
    });

    const handleChangeSelect = (name: string, value: any) => {
        formik.setFieldValue(name, value);
    };

    const handleDeleteEmployeeSelect = () => {
        const newSelectedData: any[] = [];
        employeeList?.forEach((row) => {
            if (!row?.isParentChecked) {
                newSelectedData.push({
                    ...row,
                    data: (row?.data || []).filter(
                        (oldItem: any) => !employeeChecked?.some((removeItem) => oldItem?.employeeId === removeItem?.employeeId)
                    )
                });
            }
        });

        setEmployeeList(newSelectedData);
        setEmployeeChecked([]);
    };

    const convertDataEmployeeSelect = (data: any[]): any[] => {
        return data?.map((item) => ({
            employeeId: item?.id || "",
            employeeName: item?.name || "",
            maNhanVien: item?.maNhanVien || "",
            phongBanId: item?.phongBanId || "",
            phongBanText: item?.phongBanText || "",
            chucVuId: item?.chucVuId || "",
            chucVuText: item?.chucVuText || "",
            viTriCongViecId: item?.viTriCongViecId || "",
            viTriCongViecText: item?.viTriCongViecText || "",
        }));
    };

    const groupEmployeesByPhongBan = (flatData: any[]) => {
        const result: any = [];
        const phongBanMap = new Map();
    
        flatData.forEach(emp => {
            if (!phongBanMap.has(emp.phongBanId)) {
                phongBanMap.set(emp.phongBanId, {
                    phongBanId: emp.phongBanId,
                    phongBanText: emp.phongBanText,
                    data: []
                });
                result.push(phongBanMap.get(emp.phongBanId));
            }
            phongBanMap.get(emp.phongBanId).data.push({
                employeeId: emp.employeeId,
                employeeName: emp.employeeName,
                maNhanVien: emp?.maNhanVien || "",
                phongBanId: emp?.phongBanId || "",
                phongBanText: emp?.phongBanText || "",
                chucVuId: emp?.chucVuId || "",
                chucVuText: emp?.chucVuText || "",
                viTriCongViecId: emp?.viTriCongViecId || "",
                viTriCongViecText: emp?.viTriCongViecText || "",
            });
        });
    
        return result;
    }

    const columns: columnNamesCollapseType[] = [
        {
            name: lang("TABLE.INDEX"),
            field: "stt",
            headerCellProps: {
                minWidth: 50
            },
            bodyCellProps: {
                textAlign: "center"
            },
            render: (row: any, index: number) => <span>{index + 1}</span>
        },
        {
            name: lang("INPUT.CODE"),
            field: "maNhanVien",
            headerCellProps: {
                minWidth: 120
            },
            bodyCellProps: {
                textAlign: "center"
            },
            render: (row: any) => <span className="color-steel-blue fw-600">{row?.maNhanVien}</span>
        },
        {
            name: lang("INPUT.FULLNAME"),
            field: "employeeName",
            headerCellProps: {
                minWidth: 200
            }
        },
        {
            name: lang("PROFILE.ROLE"),
            field: "chucVuText",
            headerCellProps: {
                minWidth: 250
            }
        },
        {
            name: lang("GENERAL.EMPLOYEE.TITLE"),
            field: "viTriCongViecText",
            headerCellProps: {
                minWidth: 150
            }
        },
    ];

    const columnsEmployeeSelected: columnNamesCollapseType[] = [
        {
            name: lang("INPUT.CODE"),
            field: "codeAndName",
            headerCellProps: {
                minWidth: "200px"
            },
            bodyCellProps: {
                textAlign: "left"
            },
            render: (row: any) => (
                <span>
                    <p className="color-steel-blue fw-600 spaces p-0 m-0">{row?.maNhanVien}</p>
                    <p className="spaces p-0 m-0">{row?.tenNhanVien}</p>
                </span>
            )
        },
        {
            name: lang("INPUT.SALARY.POSITION"),
            field: "chucVuText",
            headerCellProps: {
                minWidth: "200px"
            },
            bodyCellProps: {
                textAlign: "left"
            }
        },
        {
            name: lang("GENERAL.EMPLOYEE.TITLE"),
            field: "viTriCongViecText",
            headerCellProps: {
                minWidth: "200px"
            },
            bodyCellProps: {
                textAlign: "left"
            }
        }
    ];

    return (
        <Modal
            show={open}
            onHide={handleClose}
            backdrop="static"
            size="xl"
            centered
        >
            <Form onSubmit={formik.handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {scheduleConfigInfo?.id ? (isEdit ? lang("SCHEDULE_CONFIG.EDIT") : lang("SCHEDULE_CONFIG.VIEW")) : lang("SCHEDULE_CONFIG.ADD")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-4 relative spaces z-index-4">
                        <Col sm={12} md={6} lg={3}>
                            <TextValidator
                                isRequired
                                name="code"
                                lable={lang("SCHEDULE_CONFIG.CODE")}
                                type="text"
                                onChange={formik.handleChange}
                                value={formik.values?.code}
                                readOnly={!isEdit}
                                errors={formik.errors?.code}
                                touched={formik.touched?.code}
                            />
                        </Col>
                        <Col sm={12} md={6} lg={3}>
                            <Autocomplete
                                isRequired
                                lable={lang("SCHEDULE_CONFIG.TYPE")}
                                name="typeSelected"
                                value={formik.values?.typeSelected}
                                options={LIST_TYPE_SCHEDULE(lang)}
                                onChange={(selectedOption: any) => handleChangeSelect("typeSelected", selectedOption)}
                                isReadOnly={!isEdit}
                                errors={formik.errors?.typeSelected}
                                touched={formik.touched?.typeSelected}
                            />
                        </Col>
                        <Col sm={12} md={12} lg={6}>
                            <TextValidator
                                isRequired
                                name="name"
                                lable={lang("SCHEDULE_CONFIG.NAME")}
                                type="text"
                                onChange={formik.handleChange}
                                value={formik.values?.name}
                                readOnly={!isEdit}
                                errors={formik.errors?.name}
                                touched={formik.touched?.name}
                            />
                        </Col>

                        <Col sm={12} md={12} lg={12}>
                            <Row className="mt-15">
                                <TableCollapseCustom
                                    title={lang("ISSUE.LIST_EMPLOYEES")}
                                    columnNameList={columns}
                                    data={employeeList || []}
                                    nameParent="phongBanText"
                                    nameChildren="data"
                                    selectData={setEmployeeChecked}
                                    setData={setEmployeeList}
                                    selectionMode="multiple"
                                    isSelect={true}
                                    buttonAdd={isEdit}
                                    handleOpenDialog={() => setShouldOpenSelectEmployeeDialog(true)}
                                    handleDelete={handleDeleteEmployeeSelect}
                                    notDelete={!isEdit}
                                    uniquePrefix="employeeScheduleId"
                                    disabledSelect={!isEdit}
                                />
                            </Row>
                        </Col>
                    </Row>
                    {shouldOpenSelectEmployeeDialog && (
                        <SelectEmployeeByDepartment
                            title={lang("GENERAL.LIST_EMPLOYEE_BY_DEPARTMENT")}
                            open={shouldOpenSelectEmployeeDialog}
                            columnsSelected={columnsEmployeeSelected}
                            handleClose={() => setShouldOpenSelectEmployeeDialog(false)}
                            parentSelectedData={employeeList || []}
                            setParentSelectedData={setEmployeeList}
                            funcConvert={convertDataEmployeeSelect}
                            prefixNameChildren="data"
                            searchEmployeeObject={{
                                trangThaiLamViec: Object.values(STATUS_NV)
                                    .filter((status) => status !== STATUS_NV.THOI_VIEC)
                                    .join(",")
                            }}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center">
                    {(isEdit ? (
                        <GroupButton type="btn-save" />
                    ) : (hasAuthority(PERMISSIONS["DANH_MUC.CHAM_CONG.CAU_HINH_DUYET"], PERMISSION_ABILITY.UPDATE) &&
                        <GroupButton type="btn-edit" handleSaveEdit={() => setIsEdit(true)} />
                    ))}
                    <GroupButton
                        type="btn-cancel"
                        handleCloseUpdateDialog={handleClose}
                    />
                </Modal.Footer>
            </Form>
        </Modal>
    )
};

export default ScheduleConfigForm;